import type { ComponentProps, FC } from 'react';
import { cn } from 'src/utils/cn';

type Labels = {
  checked: string;
  unchecked: string;
};

const Spinner: FC = () => (
  <div
    className={`
      flex animate-spin items-center justify-center text-[rgba(16,16,16,0.3)]
    `}
  >
    ◠
  </div>
);

export type ToggleProps = {
  isChecked?: boolean;
  loading?: boolean;
  label: string;
  onChange?: () => Promise<void> | void;
  labels?: Labels;
} & ComponentProps<'button'>;

export const Toggle: FC<ToggleProps> = ({
  isChecked = false,
  onChange,
  label,
  loading = false,
  labels = { checked: 'Yes', unchecked: 'No' },
  className,
  ...rest
}) => {
  return (
    <button
      className={cn(
        `
          relative h-[26px] w-[54px] cursor-pointer rounded-3xl border
          border-solid transition-colors duration-150 ease-in-out
          disabled:cursor-not-allowed
          legacy:rounded-full legacy:duration-200
          legacy:hover:enabled:border-[#409440]
          legacy:hover:enabled:bg-[#409440]
          legacy:focus:enabled:border-[#409440]
          legacy:focus:enabled:bg-[#409440]
        `,
        isChecked ? toggleOnClasses : toggleOffClasses,
        isChecked
          ? 'legacy:border-[#5cb85c] legacy:bg-[#5cb85c]'
          : 'legacy:border-[#dfdfdf] legacy:bg-[#efefef]',
        !isChecked &&
          `
            legacy:hover:enabled:border-[#c7c7c7]
            legacy:hover:enabled:bg-[#d7d7d7]
            legacy:focus:enabled:border-[#c7c7c7]
            legacy:focus:enabled:bg-[#d7d7d7]
          `,
        className
      )}
      type="button"
      role="switch"
      aria-checked={isChecked ? 'true' : 'false'}
      aria-label={label}
      onClick={onChange}
      disabled={loading}
      {...rest}
    >
      <div
        className={cn(
          `
            absolute top-0 flex h-full w-[30px] items-center justify-center
            text-label-sm leading-none select-none
          `,
          isChecked
            ? `
              left-0
              legacy:text-white
            `
            : `
              left-[22px]
              legacy:text-[#999]
            `
        )}
      >
        {isChecked ? labels.checked : labels.unchecked}
      </div>
      <div
        className={cn(
          `
            flex h-[22px] w-[22px] items-center justify-center rounded-full
            bg-white transition-transform duration-200 ease-in-out
          `,
          isChecked
            ? 'translate-x-[29px] shadow-[-1px_1px_3px_rgba(0,0,0,0.3)]'
            : `translate-x-px shadow-[1px_1px_3px_rgba(0,0,0,0.3)]`
        )}
      >
        {loading && <Spinner />}
      </div>
    </button>
  );
};

const toggleOnClasses = cn(
  `
    border-background-support-success-default
    bg-background-support-success-default text-foreground-surface-active
    hover:border-background-support-success-subdued
    hover:bg-background-support-success-subdued
    disabled:border-border-surface-disabled
    disabled:bg-background-support-success-minimal
    disabled:text-foreground-surface-disabled
  `
);

const toggleOffClasses = cn(
  `
    bg-background-surface-default text-foreground-support-inactive
    hover:bg-background-surface-minimal
    disabled:border-border-surface-disabled
    disabled:bg-background-surface-disabled
    disabled:text-foreground-surface-disabled
  `
);
